import React                           from 'react';
import ApiCollection                   from '@widesk-core/models/ApiCollection';
import ContactContactGroupModel        from '@/models/services/network/ContactContactGroupModel';
import ContactModel                    from '@/models/services/network/ContactModel';
import RelationRequestModel            from '@/models/services/network/RelationRequestModel';
import RelationRequestStatusModel      from '@/models/services/network/RelationRequestStatusModel';
import RelationRequestStatusHistoryModel from '@/models/services/network/RelationRequestStatusHistoryModel';
import RelationModel                   from '@/models/services/network/RelationModel';
import RelationStatusModel             from '@/models/services/network/RelationStatus';
import RelationStatusHistoryModel      from '@/models/services/network/RelationStatusHistoryModel';
import EntityContactForm               from '@/pages/Entity/forms/EntityContactForm';
import FileModel                       from '@/models/services/file/FileModel';
import Modal                           from '@/components/modal/Modal';
import { ModalData }                   from '@/components/modal/Modal';
import { Text }                        from '@chakra-ui/react';
import { toaster }                     from '@/components/ui/toaster';
import { useTranslation }              from 'react-i18next';

interface UseEntityContactsBoxParams {
	entityUrn: string;
	onSuccess: () => void;
	relationRequestStatusCollection: ApiCollection<RelationRequestStatusModel>;
	relationStatusCollection: ApiCollection<RelationStatusModel>;
}

const useEntityContactsBox = ({ entityUrn, onSuccess, relationRequestStatusCollection, relationStatusCollection }: UseEntityContactsBoxParams) => {
	const { t } = useTranslation();
	const [modalData, setModalData]     = React.useState<ModalData | undefined>();
	const [isModalOpen, setIsModalOpen] = React.useState(false);

	const closeModal = () => {
		setIsModalOpen(false);
		setModalData(undefined);
	};

	const openAddModal = () => {
		setModalData({
			setIsOpen:      setIsModalOpen,
			titleComponent: <Text>{t('pages.entity.contacts.modals.add.title')}</Text>,
			bodyComponent:  (
				<EntityContactForm
					entityUrn={entityUrn}
					onSuccess={async () => { onSuccess(); closeModal(); }}
					onClose={closeModal}
				/>
			),
			onClose: closeModal,
		});
		setIsModalOpen(true);
	};

	const openEditModal = (contact: ContactModel, file?: FileModel) => {
		setModalData({
			model:          contact,
			setIsOpen:      setIsModalOpen,
			titleComponent: <Text>{t('pages.entity.contacts.modals.edit.title')}</Text>,
			bodyComponent:  (
				<EntityContactForm
					contact={contact}
					entityUrn={entityUrn}
					file={file}
					onSuccess={async () => { onSuccess(); closeModal(); }}
					onClose={closeModal}
				/>
			),
			onClose: closeModal,
		});
		setIsModalOpen(true);
	};

	const openDeleteModal = (contact: ContactModel) => {
		setModalData({
			model:          contact,
			isDangerous:    true,
			setIsOpen:      setIsModalOpen,
			titleComponent: <Text>{t('pages.entity.contacts.modals.delete.title')}</Text>,
			bodyComponent:  <Text>{t('pages.entity.contacts.modals.delete.message', { fullname: contact.fullName })}</Text>,
			onClose:        closeModal,
			onConfirm:      async () => { await contact.destroy(); },
			onSuccess:      () => {
				toaster.create({ title: t('pages.entity.contacts.modals.delete.messages.success'), type: 'success' });
				onSuccess();
				closeModal();
			},
			onError: () => {
				toaster.create({ title: t('pages.entity.contacts.modals.delete.messages.error'), type: 'error' });
			},
		});
		setIsModalOpen(true);
	};

	const openUnassignModal = (contact: ContactModel) => {
		setModalData({
			model:          contact,
			isDangerous:    true,
			setIsOpen:      setIsModalOpen,
			titleComponent: <Text>{t('pages.entity.managers.modals.delete.title')}</Text>,
			bodyComponent:  <Text>{t('pages.entity.managers.modals.delete.message', { fullname: contact.fullName })}</Text>,
			onClose:        closeModal,
			onConfirm:      async () => {
				const ccgCollection = await new ApiCollection(ContactContactGroupModel)
					.setFilter('contact', contact.id)
					.list();
				const ccg = ccgCollection.first();
				if (ccg) await (ccg as unknown as ContactContactGroupModel).destroy();
			},
			onSuccess: () => {
				toaster.create({ title: t('pages.entity.managers.modals.delete.messages.success'), type: 'success' });
				onSuccess();
				closeModal();
			},
			onError: () => {
				toaster.create({ title: t('pages.entity.managers.modals.delete.messages.error'), type: 'error' });
			},
		});
		setIsModalOpen(true);
	};

	const openSendRelationRequestModal = (contact: ContactModel) => {
		setModalData({
			model:          contact,
			setIsOpen:      setIsModalOpen,
			titleComponent: <Text>{t('pages.entity.contacts.modals.sendRelationRequest.title')}</Text>,
			bodyComponent:  <Text>{t('pages.entity.contacts.modals.sendRelationRequest.message', { email: contact.email })}</Text>,
			onClose:        closeModal,
			onConfirm: async () => {
				const newRelationRequest = new RelationRequestModel();
				await newRelationRequest.patch({
					contact: contact.urn,
					email:   contact.email,
				});
			},
			onSuccess: () => {
				toaster.create({ title: t('pages.entity.contacts.modals.sendRelationRequest.messages.success'), type: 'success', duration: 5000 });
				onSuccess();
				closeModal();
			},
			onError: () => {
				toaster.create({ title: t('pages.entity.contacts.modals.sendRelationRequest.messages.error'), type: 'error', duration: 5000 });
			},
		});
		setIsModalOpen(true);
	};

	const openCancelRelationRequestModal = (contact: ContactModel, relationRequest: RelationRequestModel) => {
		setModalData({
			isDangerous:    true,
			setIsOpen:      setIsModalOpen,
			titleComponent: <Text>{t('pages.entity.contacts.modals.cancelRelationRequest.title')}</Text>,
			bodyComponent:  <Text>{t('pages.entity.contacts.modals.cancelRelationRequest.message', { fullname: contact.fullName })}</Text>,
			onClose:        closeModal,
			onConfirm: async () => {
				const canceledStatus = relationRequestStatusCollection.find(m => m.reference === 'canceled') as RelationRequestStatusModel;
				const history = new RelationRequestStatusHistoryModel();
				await history.patch({
					relationRequest:       relationRequest.urn,
					relationRequestStatus: canceledStatus.urn,
				});
			},
			onSuccess: () => {
				toaster.create({ title: t('pages.entity.contacts.modals.cancelRelationRequest.messages.success'), type: 'success' });
				onSuccess();
				closeModal();
			},
			onError: () => {
				toaster.create({ title: t('pages.entity.contacts.modals.cancelRelationRequest.messages.error'), type: 'error' });
			},
		});
		setIsModalOpen(true);
	};

	const openDissociateModal = (contact: ContactModel, relation: RelationModel) => {
		setModalData({
			isDangerous:    true,
			setIsOpen:      setIsModalOpen,
			titleComponent: <Text>{t('pages.entity.contacts.modals.dissociate.title')}</Text>,
			bodyComponent:  <Text>{t('pages.entity.contacts.modals.dissociate.message', { fullname: contact.fullName })}</Text>,
			onClose:        closeModal,
			onConfirm: async () => {
				const deletedStatus = relationStatusCollection.find(m => m.reference === 'deleted') as RelationStatusModel;
				const history = new RelationStatusHistoryModel();
				await history.patch({
					relation:       relation.urn,
					relationStatus: deletedStatus.urn,
				});
			},
			onSuccess: () => {
				toaster.create({ title: t('pages.entity.contacts.modals.dissociate.messages.success'), type: 'success' });
				onSuccess();
				closeModal();
			},
			onError: () => {
				toaster.create({ title: t('pages.entity.contacts.modals.dissociate.messages.error'), type: 'error' });
			},
		});
		setIsModalOpen(true);
	};

	const modalElement = (
		<Modal
			model={modalData?.model}
			isDangerous={modalData?.isDangerous}
			isOpen={isModalOpen}
			setIsOpen={setIsModalOpen}
			titleComponent={modalData?.titleComponent}
			bodyComponent={modalData?.bodyComponent}
			onConfirm={modalData?.onConfirm}
			onClose={modalData?.onClose}
			onError={modalData?.onError}
			onSuccess={modalData?.onSuccess}
		/>
	);

	return { modalElement, openAddModal, openEditModal, openDeleteModal, openUnassignModal, openSendRelationRequestModal, openCancelRelationRequestModal, openDissociateModal };
};

export default useEntityContactsBox;
